import React from "react";
import { Redirect } from "react-router-dom";
import { Button, Segment } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { OrganizationBillingFlags } from "../../api/lib";
import { useAuthContext } from "../../auth/AuthContextProvider";
import { Routes } from "../../routes";
import { useTrackingContext } from "../../tracking/TrackingContextProvider";
import { Loading, Section, SectionContent, SectionHead, SectionHeader, TextLink } from "../../ui/lib";
import { Permission } from "../../util/PermissionCache";
import { useWithRefresh } from "../../util/WithRefreshContext";
import { useDashboardContext } from "../DashboardContextProvider";
import { useCheckPermissionByUrl } from "../sidebar/useCheckPermissionByUrl";
import BillingDetails from "./BillingDetails";
import EverestInvoices from "./EverestInvoices";
import Invoices from "./Invoices";
import PaymentMethods from "./PaymentMethods";
import { useMissingPermissionsToast } from "../../util/hooks/useMissingPermissionsToast";

const useBillingFlags = () => {
  const [billingFlags, setBillingFlags] = React.useState<OrganizationBillingFlags | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const { selectedOrganization } = useDashboardContext();
  const fetchBillingFlags = async () => {
    const idOptions = { id: selectedOrganization?.id };
    setIsLoading(true);
    const billingFlags = await apiClients.billingClient.GetOrganizationBillingFlags(idOptions);
    setBillingFlags(billingFlags);
    setIsLoading(false);
  };
  React.useEffect(() => {
    fetchBillingFlags();
  }, [selectedOrganization?.id]);
  return { billingFlags, isLoading };
};

export const OrganizationBilling = () => {
  const { checkOrgPermission } = useCheckPermissionByUrl();
  const isBillingConfigGetAllowed = checkOrgPermission("billing.config.get");
  const isBillingConfigSetAllowed = checkOrgPermission("billing.config.set");
  const { isFreeTier } = useDashboardContext();
  const { billingFlags, isLoading: isLoadingBillingFlags } = useBillingFlags();
  const hasBillingFlags = !!billingFlags;
  const is_allowed_to_access_payment_methods = !!billingFlags?.is_allowed_to_access_payment_methods;
  const is_allowed_to_list_invoices = !!billingFlags?.is_allowed_to_list_invoices;
  const isPaymentMethodListAllowed = is_allowed_to_access_payment_methods && checkOrgPermission("billing.paymentmethod.list");
  const isPaymentMethodCreateAllowed = is_allowed_to_access_payment_methods && checkOrgPermission("billing.paymentmethod.create");
  const isPaymentMethodUpdateAllowed = is_allowed_to_access_payment_methods && checkOrgPermission("billing.paymentmethod.update");
  const isPaymentMethodDeleteAllowed = is_allowed_to_access_payment_methods && checkOrgPermission("billing.paymentmethod.delete");
  const isInvoiceListAllowed = is_allowed_to_list_invoices && checkOrgPermission("billing.invoice.list");
  const isInvoicePreliminaryViewAllowed = is_allowed_to_list_invoices && checkOrgPermission("billing.invoice.get-preliminary");

  const showBillingTab = hasBillingFlags && (isBillingConfigGetAllowed || isPaymentMethodListAllowed || isInvoiceListAllowed);

  const [billingConfigValid, setBillingConfigValid] = React.useState<boolean>(true);
  // When the org has new-billing (Everest) invoices, the plain "Invoices" name goes
  // to those and the V1 section becomes "Invoices (legacy billing)".
  const [everestHasInvoices, setEverestHasInvoices] = React.useState<boolean>(false);
  const onBillingConfigChanged = (isValid: boolean) => {
    setBillingConfigValid(isValid);
  };
  const { refreshNow } = useWithRefresh();
  const { selectedOrganization, onReloadOrganization } = useDashboardContext();
  const { auth } = useAuthContext();
  const { tracking } = useTrackingContext();

  useMissingPermissionsToast({
    isLoading: isLoadingBillingFlags,
    deps: [selectedOrganization.id],
    getMissingPermissionsMessage: () => {
      if (showBillingTab) {
        return undefined;
      }
      const requiredPermissions = ["billing.config.get", "billing.paymentmethod.list", "billing.invoice.list"] as Permission[];
      const missingPermissions = requiredPermissions.filter((permission) => !checkOrgPermission(permission));
      if (missingPermissions.length > 0) {
        return `Missing permissions: ${missingPermissions.join(", ")}`;
      }
      return "You don't have permission to access the billing tab, please check the billing flags";
    },
  });

  if (isLoadingBillingFlags) {
    return <Loading />;
  }
  if (!showBillingTab) {
    return <Redirect to={Routes.dashboard_organization_overviewWithId(selectedOrganization.id || "")} />;
  }
  return (
    <div>
      {isFreeTier && (
        <Section>
          <SectionHead>
            <SectionHeader title="Unlock All Features" />
          </SectionHead>
          <SectionContent>
            <Segment>
              <p className="para">To unlock all Arango Managed Platform functionality, enter your billing address, then add a payment method.</p>
              <p className="para">
                As soon as you've added a payment method, all Arango Managed Platform functionality is immediately unlocked. From that point on, your
                deployments will no longer expire and you can create more &amp; larger deployments.
              </p>
              <Button as={TextLink} onClick={() => window.location.replace(Routes.dashboard_pricing)}>
                See all Arango Managed Platform packages
              </Button>
            </Segment>
          </SectionContent>
        </Section>
      )}
      {isBillingConfigGetAllowed && (
        <BillingDetails
          api={apiClients.billingClient}
          isEditAllowed={!!isBillingConfigSetAllowed}
          onBillingConfigChanged={onBillingConfigChanged}
          isPaymentMethodListAllowed={!!isPaymentMethodListAllowed}
          isPaymentMethodCreateAllowed={!!isPaymentMethodCreateAllowed}
        />
      )}
      {isPaymentMethodListAllowed && (
        <PaymentMethods
          organization={selectedOrganization}
          onReloadOrganization={onReloadOrganization}
          tracking={tracking}
          refreshNow={refreshNow}
          api={apiClients.billingClient}
          isAddAllowed={!!isPaymentMethodCreateAllowed}
          isAddEnabled={billingConfigValid}
          isUpdateAllowed={!!isPaymentMethodUpdateAllowed}
          isDeleteAllowed={!!isPaymentMethodDeleteAllowed}
        />
      )}
      {isInvoiceListAllowed && (
        <Invoices
          refreshNow={refreshNow}
          auth={auth}
          organization={selectedOrganization}
          isInvoicePreliminaryViewAllowed={!!isInvoicePreliminaryViewAllowed}
          invoiceAPI={apiClients.billingClient}
          title={everestHasInvoices ? "Invoices (legacy billing)" : "Invoices"}
        />
      )}
      {isInvoiceListAllowed && (
        <EverestInvoices refreshNow={refreshNow} auth={auth} organization={selectedOrganization} onHasInvoicesChange={setEverestHasInvoices} />
      )}
    </div>
  );
};
